import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {GridBlockHeaderOrNull, GridBlockBody} from 'analysis-tool/data-layout/GridBlock';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import Pane, {PaneBorder, HeaderBorder} from 'ui-library/pane/Pane';

import {useDataViewContext} from './DataViewContext';
import DataViewToolbar from './DataViewToolbar';

interface DataViewPaneProps {
  children: React.ReactNode;
}

/**
 * Wrapper for `Pane` that should be used by all subcomponents of `DataView`.
 *
 * TODO(BNCH-121029): Implement functionality for this component.
 *
 * @package
 */
export default function DataViewPane({children}: DataViewPaneProps): JSX.Element {
  const {onTitleChange, title, blockConfig, isChartView, dataTestAttrs, dragHandleProps} =
    useDataViewContext();

  const blockContent = (
    <GridBlockBody
      isCollapsible={blockConfig?.isCollapsible ?? undefined}
      isInLayout={isChartView && blockConfig?.isInLayout}
    >
      {children}
    </GridBlockBody>
  );

  if (blockConfig?.blockMode === 'CONTENT_ONLY') {
    return blockContent;
  }

  let titleField: React.ReactNode;
  if (blockConfig) {
    titleField = (
      <GridBlockHeaderOrNull
        title={blockConfig.title ?? title}
        subtitle={blockConfig.subtitle ?? undefined}
        infoText={blockConfig.infoText ?? undefined}
      />
    );
  } else if (onTitleChange != null) {
    titleField = (
      <EditableTextField
        value={title}
        requestChange={(newTitle: string) => {
          if (newTitle !== title) {
            onTitleChange(newTitle);
          }
        }}
        dataTestAttrs={{component: 'DataViewPane', element: 'editableTextField'}}
      />
    );
  } else {
    titleField = title;
  }

  const inlineDragHandle = dragHandleProps ? (
    <div
      className={classNames(dragHandleProps.className, css(styles.inlineDragHandle))}
      onMouseDown={dragHandleProps.onMouseDown}
    >
      <BenchlingIcon icon="drag-handle" className={css(styles.inlineDragHandleIcon)} />
    </div>
  ) : null;

  return (
    <Pane
      paneBorder={blockConfig?.isInLayout ? PaneBorder.NO_BORDER : PaneBorder.BOX_SHADOW}
      headerBorder={blockConfig?.isInLayout ? HeaderBorder.NONE : HeaderBorder.SOLID}
      title={
        <div className={css(styles.badgeContainer)}>
          {inlineDragHandle}
          {titleField}
        </div>
      }
      toolbar={<DataViewToolbar />}
      dataTestAttrs={dataTestAttrs}
    >
      {blockContent}
    </Pane>
  );
}

const styles = StyleSheet.create({
  badgeContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  inlineDragHandle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    cursor: 'grab',
    borderRadius: 4,
    marginRight: 4,
    flexShrink: 0,
    ':hover': {
      backgroundColor: Color.GRAY_100,
    },
  },
  inlineDragHandleIcon: {
    fontSize: 14,
    color: Color.GRAY_400,
    userSelect: 'none',
  },
});
